"use client";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Send, Trash2 } from "lucide-react";
import { useCommsPanel } from "../CommsProvider";

/** Lightweight markdown-to-HTML for copilot responses (bold, italic, lists, line breaks). */
function renderMarkdown(text: string): string {
  return text
    // escape HTML
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    // bold: **text** or __text__
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.+?)__/g, "<strong>$1</strong>")
    // italic: *text* or _text_ (but not inside bold)
    .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "<em>$1</em>")
    // unordered list items: - item or * item at start of line
    .replace(/^[-*] (.+)$/gm, '<li class="ml-3 list-disc">$1</li>')
    // ordered list items: 1. item
    .replace(/^\d+\.\s+(.+)$/gm, '<li class="ml-3 list-decimal">$1</li>')
    // line breaks
    .replace(/\n/g, "<br />");
}

interface CopilotMessage {
  id: string;
  role: string;
  content: string;
  createdAt: string;
}

export function CopilotTab() {
  const { activeLeadId } = useCommsPanel();
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchThread = useCallback(async () => {
    if (!activeLeadId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/leads/${activeLeadId}/copilot`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data?.messages ?? []);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [activeLeadId]);

  useEffect(() => {
    fetchThread();
  }, [fetchThread]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  async function handleSend() {
    if (!input.trim() || !activeLeadId || sending) return;
    const content = input.trim();
    setInput("");
    setSending(true);

    // Optimistic: add user message
    const tempMsg: CopilotMessage = {
      id: `temp-${Date.now()}`,
      role: "user",
      content,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMsg]);

    try {
      const res = await fetch(`/api/leads/${activeLeadId}/copilot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data?.messages ?? []);
      }
    } catch {
      // ignore
    } finally {
      setSending(false);
    }
  }

  async function handleClear() {
    if (!activeLeadId) return;
    try {
      await fetch(`/api/leads/${activeLeadId}/copilot`, { method: "DELETE" });
      setMessages([]);
    } catch {
      // ignore
    }
  }

  return (
    <div className="flex h-full flex-col bg-violet-50/30">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-violet-100 px-3 py-2">
        <p className="text-xs font-medium text-violet-700">AI Copilot</p>
        {messages.length > 0 && (
          <button
            onClick={handleClear}
            className="flex items-center gap-1 rounded px-2 py-1 text-[10px] text-violet-500 hover:bg-violet-100 hover:text-violet-700"
          >
            <Trash2 className="h-3 w-3" />
            Clear
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {loading ? (
          <div className="flex h-full items-center justify-center text-xs text-violet-400">
            Loading copilot...
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <p className="text-xs text-violet-400">
              Ask questions about this lead
            </p>
            <p className="text-[10px] text-violet-300">
              &quot;What&apos;s the best approach for this lead?&quot; &bull; &quot;Summarize their history&quot;
            </p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed ${
                  msg.role === "user"
                    ? "bg-gray-200 text-gray-900"
                    : "bg-violet-600 text-white"
                }`}
              >
                {msg.role === "user" ? (
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                ) : (
                  <div
                    className="whitespace-pre-wrap [&_strong]:font-semibold [&_em]:italic [&_li]:my-0.5"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                  />
                )}
                <p
                  className={`mt-1 text-[9px] ${
                    msg.role === "user" ? "text-gray-500" : "text-violet-200"
                  }`}
                >
                  {new Date(msg.createdAt).toLocaleTimeString("en-ZA", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            </div>
          ))
        )}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-lg bg-violet-600 px-3 py-2">
              <div className="flex gap-1">
                <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-violet-200" />
                <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-violet-200" style={{ animationDelay: "0.1s" }} />
                <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-violet-200" style={{ animationDelay: "0.2s" }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-violet-100 bg-white px-3 py-2">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder="Ask about this lead..."
            className="flex-1 rounded-lg border border-violet-200 px-3 py-2 text-xs focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
            disabled={sending}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || sending}
            className="rounded-lg bg-violet-600 p-2 text-white hover:bg-violet-700 disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
